import React, { useEffect } from "react";
import { ErrorMessage, Field, Formik, Form } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DefaultButton from "../../../shared/components/DefaultButton";
import { setTokenToHeader } from "../../../shared/utilites/ApiClient";
import { loadLoginDataRequest } from "../redux/Actions";

// Adjust these to your actual login routes
const PORTAL_ROUTES = {
  admin: "/login",
  report: "/powerbi-portal/login",
};

const PORTALS = {
  admin: {
    title: "Admin Portal",
    desc: "Manage users, systems and configurations",
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
      </svg>
    ),
  },
  report: {
    title: "Report Portal",
    desc: "View and analyze reports",
    icon: (
      <svg viewBox="0 0 24 24">
        <line x1="6" y1="20" x2="6" y2="12" />
        <line x1="12" y1="20" x2="12" y2="6" />
        <line x1="18" y1="20" x2="18" y2="14" />
        <line x1="3" y1="20" x2="21" y2="20" />
      </svg>
    ),
  },
};

const Login = ({ portalType = "admin" }) => {
  const logoPath = "/logo.png";
  const markPath = "/mark-dark.png";
  const partnerLogos = [
    { src: "/Renew.png", alt: "Renew Fitness" },
    { src: "/Zax.png", alt: "Zax" },
    { src: "/Buddys.png", alt: "Buddy's" },
    { src: "/WV.png", alt: "Wireless Vision" },
    { src: "/earnie.png", alt: "Earnie" },
  ];

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoggedIn, login, roleAccess, redirect, error } = useSelector((state) => state.login);
  const activePortal = PORTALS[portalType] || PORTALS.admin;

  useEffect(() => {
    localStorage.setItem("portal_type", portalType);
  }, [portalType]);

  useEffect(() => {
    let toPath;

    if (redirect) {
      toPath = error?.msg?.redirect_link;
      navigate(toPath, { replace: true });
    } else if (isLoggedIn) {
      setTokenToHeader(login.token);

      if (portalType === "report") {
        toPath = "/powerbi-portal";
      } else {
        const returnUrl = sessionStorage.getItem("returnUrl");

        if (returnUrl) {
          sessionStorage.removeItem("returnUrl");
          toPath = returnUrl.includes("/powerbi-portal") ? "/dashboard" : returnUrl;
        } else if (login?.company_name === "Operis") {
          toPath = "/seatbooking";
        } else if (login?.role === "hc_sales") {
          toPath = "/myplan";
        } else {
          toPath = "/dashboard";
        }
      }

      navigate(toPath, { replace: true });
    }
  }, [isLoggedIn, dispatch, login, roleAccess, redirect, navigate, portalType]);

  const validate = (values) => {
    const errors = {};
    if (!values.employee_id) errors.employee_id = "Required";
    if (!values.password) errors.password = "Required";
    return errors;
  };

  const submit = (values) => {
    dispatch(loadLoginDataRequest(values));
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        {/* ---------- Left: brand ---------- */}
        <div className="login-hero">
          <img src={logoPath} alt="Operis" className="login-hero-logo" />
          <span className="login-hero-rule" />

          <div className="login-portals">
            {Object.entries(PORTALS).map(([key, p]) => (
              <Link
                key={key}
                to={PORTAL_ROUTES[key]}
                className={`login-portal ${portalType === key ? "active" : ""}`}
              >
                <span className="login-portal-icon">{p.icon}</span>
                <span className="login-portal-text">
                  <strong>{p.title}</strong>
                  <small>{p.desc}</small>
                </span>
                <svg className="login-portal-arrow" viewBox="0 0 24 24">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="13 6 19 12 13 18" />
                </svg>
              </Link>
            ))}
          </div>

          <div className="login-partners">
            <div className="login-partners-label">In partnership with</div>
            <div className="login-partners-strip">
              {partnerLogos.map((l) => (
                <img key={l.src} src={l.src} alt={l.alt} />
              ))}
            </div>
          </div>
        </div>

        {/* ---------- Right: form ---------- */}
        <div className="login-card">
          <div className="login-brand">
            <img src={markPath} alt="" aria-hidden="true" className="login-logo" />
            <h1>Sign In</h1>
            <p className="login-subtitle">Access the Operis {activePortal.title}</p>
          </div>

          <div className="login-portal-pill">
            <span className="login-portal-icon">{activePortal.icon}</span>
            <span className="login-portal-text">
              <strong>{activePortal.title}</strong>
              <small>{activePortal.desc}</small>
            </span>
          </div>

          <Formik
            validate={validate}
            initialValues={{ employee_id: "", password: "" }}
            onSubmit={submit}
          >
            {({ errors, touched }) => (
              <Form>
                <div className="login-field">
                  <label htmlFor="employee_id">User ID</label>
                  <div className="login-input-wrap">
                    <svg className="login-input-icon" viewBox="0 0 24 24">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c1.5-4 4.5-6 8-6s6.5 2 8 6" />
                    </svg>
                    <Field
                      id="employee_id"
                      name="employee_id"
                      placeholder="Enter your user ID"
                      autoComplete="username"
                      autoFocus
                      className={`login-input ${touched.employee_id && errors.employee_id ? "is-invalid" : ""}`}
                    />
                  </div>
                  <ErrorMessage name="employee_id" component="div" className="login-error" />
                </div>

                <div className="login-field">
                  <label htmlFor="password">Password</label>
                  <div className="login-input-wrap">
                    <svg className="login-input-icon" viewBox="0 0 24 24">
                      <rect x="5" y="11" width="14" height="9" rx="2" />
                      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                    </svg>
                    <Field
                      id="password"
                      type="password"
                      name="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className={`login-input ${touched.password && errors.password ? "is-invalid" : ""}`}
                    />
                  </div>
                  <ErrorMessage name="password" component="div" className="login-error" />
                </div>

                <Link to="/reset" className="login-forgot">Reset Password</Link>

                <DefaultButton
                  buttonTitle="Log In"
                  buttonClass="default-btn-color login-btn"
                  buttonType="submit"
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Login;